/**
 * VoiceEncodingRenderer (T-060, ARCH-005 §1/§9, TECH-09, AI-01).
 *
 * Renders the Zod-validated voice_encoding payload: a voice-attribute card
 * stack, a banned-phrase list, and a side-by-side on-voice vs off-voice
 * comparison block (T-060 AC: "Sample comparison block uses side-by-side
 * layout"). Body only.
 *
 * Verbosity (ARCH-005 §5): 'recommend' shows attribute names + definitions +
 * banned phrases; 'show_me' adds do/don't examples + the side-by-side
 * comparison; 'build_it_with_me' adds the methodology callout.
 */

import {
  FieldList,
  MethodologyCallout,
  SectionHead,
  type RendererProps,
} from '../shared';
import type { VoiceEncodingContent } from './payload';

export function VoiceEncodingRenderer({
  payload,
  verbosity,
}: RendererProps<VoiceEncodingContent>) {
  const full = verbosity !== 'recommend';
  return (
    <div className="space-y-6">
      <SectionHead>Voice</SectionHead>
      <div className="space-y-4">
        {payload.voice_attributes.map((a, i) => (
          <div
            key={i}
            className="rounded-md border border-[color:var(--color-rule)] p-4"
          >
            <p className="font-semibold text-[color:var(--color-navy)]">
              {a.name}
            </p>
            <p className="text-sm text-[color:var(--color-text-mute)]">
              {a.definition}
            </p>
            {full ? (
              <div className="mt-2 grid gap-3 text-sm sm:grid-cols-2">
                <FieldList title="Do" items={a.do_examples} />
                <FieldList title="Don't" items={a.dont_examples} />
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {payload.banned_phrases.length > 0 ? (
        <FieldList title="Banned phrases" items={payload.banned_phrases} />
      ) : null}

      {full && payload.sample_comparisons.length > 0 ? (
        <div className="space-y-3">
          <SectionHead>On-voice vs off-voice</SectionHead>
          {payload.sample_comparisons.map((s, i) => (
            <div
              key={i}
              className="rounded-md border border-[color:var(--color-rule)] p-3 text-sm"
            >
              <p className="mb-2 text-xs uppercase tracking-wide text-[color:var(--color-text-mute)]">
                {s.context}
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-sm bg-[color:var(--color-linen)]/40 p-2">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-navy)]">
                    On voice
                  </p>
                  <p className="text-[color:var(--color-navy)]">
                    {s.on_voice}
                  </p>
                </div>
                <div className="rounded-sm border border-dashed border-[color:var(--color-rule)] p-2">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-mute)]">
                    Off voice
                  </p>
                  <p className="text-[color:var(--color-text-mute)]">
                    {s.off_voice}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <MethodologyCallout verbosity={verbosity}>
        <p>
          The do/don't pairs are the encoding. When you write the next thing,
          read it back against the off-voice column first. If it could have come
          from that column, it is not in your voice yet.
        </p>
      </MethodologyCallout>
    </div>
  );
}
